import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";

export type FilterTagOption = {
  value: string;
  label: string;
  count?: number;
};

/**
 * Eine Filterzeile aus Tags (Artikel, Entwurf A3): die wichtigsten Werte direkt
 * anklickbar, alle übrigen über „Alle … ▾“ mit Suche und Häkchen. Gewählte
 * Werte stehen immer vorne als grüne Tags. Mehrfachauswahl; auf dem Handy
 * bleiben nur gewählte Tags und die Auswahlliste sichtbar.
 */
export function FilterTagRow({
  label,
  options,
  selected,
  onToggle,
  onClear,
  top = 6,
  allLabel,
  pickerOptions,
  onPickerSearch,
  pickerLoading = false,
  selectedLabel,
}: {
  label: string;
  /** Reihenfolge = Wichtigkeit; die ersten `top` stehen als Tags da. */
  options: readonly FilterTagOption[];
  selected: readonly string[];
  onToggle: (value: string) => void;
  onClear: () => void;
  top?: number;
  /** z. B. „Alle 42 Hersteller“; ohne Wert gibt es keine Auswahlliste. */
  allLabel?: string;
  /** Eigene Liste für die Auswahl (z. B. serverseitig gesucht); sonst `options`. */
  pickerOptions?: readonly FilterTagOption[];
  onPickerSearch?: (query: string) => void;
  pickerLoading?: boolean;
  /** Name eines gewählten Werts, der gerade nicht in `options` steht. */
  selectedLabel?: (value: string) => string;
}) {
  const byValue = new Map(options.map((option) => [option.value, option]));
  const topValues = options.slice(0, top).map((option) => option.value);
  const shown = [...new Set([...selected, ...topValues])];
  const withPicker = Boolean(allLabel) && options.length + (pickerOptions ? 1 : 0) > top;
  return (
    <div className="flex flex-wrap items-center gap-2 py-1.5" role="group" aria-label={label}>
      <span className="w-full shrink-0 text-sm text-slate-500 sm:w-28">{label}</span>
      {shown.length === 0 ? <span className="text-sm text-slate-400">–</span> : null}
      {shown.map((value) => {
        const on = selected.includes(value);
        const name = byValue.get(value)?.label ?? selectedLabel?.(value) ?? value;
        return (
          <button
            key={value}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(value)}
            className={`${on ? "inline-flex" : withPicker ? "hidden sm:inline-flex" : "inline-flex"} h-9 max-w-full items-center gap-1 truncate rounded-full border px-3.5 text-sm ${
              on
                ? "border-emerald-700 bg-emerald-50 font-medium text-emerald-900 hover:bg-emerald-100"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <span className="truncate">{name}</span>
            {on ? <span aria-hidden="true" className="opacity-80">×</span> : null}
          </button>
        );
      })}
      {withPicker && allLabel ? (
        <FilterPicker
          label={label}
          buttonLabel={allLabel}
          options={pickerOptions ?? options}
          selected={selected}
          onToggle={onToggle}
          onClear={onClear}
          onSearch={onPickerSearch}
          loading={pickerLoading}
        />
      ) : null}
    </div>
  );
}

function FilterPicker({
  label,
  buttonLabel,
  options,
  selected,
  onToggle,
  onClear,
  onSearch,
  loading,
}: {
  label: string;
  buttonLabel: string;
  options: readonly FilterTagOption[];
  selected: readonly string[];
  onToggle: (value: string) => void;
  onClear: () => void;
  onSearch?: (query: string) => void;
  loading: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dialogId = useId();
  const [leftOffset, setLeftOffset] = useState(0);

  useLayoutEffect(() => {
    if (!open) return;
    function position() {
      const root = rootRef.current;
      if (!root) return;
      const left = root.getBoundingClientRect().left;
      const width = Math.min(320, window.innerWidth - 48);
      setLeftOffset(Math.max(24, Math.min(left, window.innerWidth - 24 - width)) - left);
    }
    position();
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [open, selected, buttonLabel]);

  function close(restoreFocus: boolean) {
    setOpen(false);
    setQuery("");
    onSearch?.("");
    if (restoreFocus) buttonRef.current?.focus();
  }

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    function onPointer(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) close(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") close(true);
    }
    document.addEventListener("pointerdown", onPointer, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer, true);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const needle = query.trim().toLowerCase();
  // A server search already filtered the list; a local one filters here.
  const visible = (onSearch ? [...options] : options.filter((option) => option.label.toLowerCase().includes(needle)))
    .sort((a, b) => a.label.localeCompare(b.label, "de"));
  const hasSelection = selected.length > 0;

  return (
    <span ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? dialogId : undefined}
        onClick={() => (open ? close(false) : setOpen(true))}
        className={`inline-flex h-9 items-center gap-1 rounded-full border px-3.5 text-sm ${
          open || hasSelection
            ? "border-emerald-700 bg-emerald-50 font-medium text-emerald-900"
            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
        }`}
      >
        {buttonLabel} <span aria-hidden="true" className="text-slate-400">▾</span>
      </button>
      {open ? (
        <div
          id={dialogId}
          role="dialog"
          aria-label={`${label} wählen`}
          style={{ left: leftOffset }}
          className="absolute top-9 z-40 w-[min(20rem,calc(100vw-3rem))] rounded-lg border border-slate-200 bg-white p-2 shadow-lg"
        >
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              onSearch?.(event.target.value);
            }}
            placeholder={`${label} suchen …`}
            aria-label={`${label} suchen`}
            className="h-9 w-full rounded-md border border-slate-300 px-3 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          />
          <ul className="mt-1.5 max-h-64 overflow-y-auto">
            {visible.length === 0 ? (
              <li className="px-2 py-2.5 text-sm text-slate-500">{loading ? `Lade ${label} …` : "Keine Treffer."}</li>
            ) : (
              visible.map((option) => (
                <li key={option.value}>
                  <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-800 hover:bg-slate-100">
                    <input
                      type="checkbox"
                      checked={selected.includes(option.value)}
                      onChange={() => onToggle(option.value)}
                      className="h-4 w-4 accent-emerald-700"
                    />
                    <span className="min-w-0 flex-1 truncate">{option.label}</span>
                    {option.count != null ? (
                      <span className="text-xs tabular-nums text-slate-400">{option.count.toLocaleString("de-AT")}</span>
                    ) : null}
                  </label>
                </li>
              ))
            )}
          </ul>
          <div className="mt-1.5 flex justify-between border-t border-slate-100 pt-1.5">
            <button type="button" onClick={onClear} disabled={!hasSelection} className="px-2 py-1 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-40">
              Auswahl aufheben
            </button>
            <button type="button" onClick={() => close(true)} className="px-2 py-1 text-sm font-medium text-emerald-700 hover:underline">
              Fertig
            </button>
          </div>
        </div>
      ) : null}
    </span>
  );
}

/** Einfachauswahl als Tags, z. B. „Gekauft: Letzte 3 Monate“. */
export function FilterChoiceRow<T extends string | number>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly { value: T; label: string }[];
  value: T | null;
  onChange: (next: T | null) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 py-1.5" role="group" aria-label={label}>
      <span className="w-full shrink-0 text-sm text-slate-500 sm:w-28">{label}</span>
      {options.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={String(option.value)}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? null : option.value)}
            className={`inline-flex h-9 items-center gap-1 rounded-full border px-3.5 text-sm ${
              on
                ? "border-emerald-700 bg-emerald-50 font-medium text-emerald-900 hover:bg-emerald-100"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            {option.label}
            {on ? <span aria-hidden="true" className="opacity-80">×</span> : null}
          </button>
        );
      })}
    </div>
  );
}
